/**
 * Goal 2: copy_report as one dialog-owned operation (copy + optional checklist).
 * Checklist failure after successful copy → PARTIAL (SPEC false-success).
 */
import {
  getOperationCoordinator,
  STEP_STATUS,
  OUTCOME,
  normalizeError,
  hasMeaningfulErrors,
  canConfirmStepFromEvidence,
} from "../../../../operationFeedback";
import { copyReport } from "../../../../api/service/report";

export const COPY_REPORT_OPERATION_KEY = "copy_report";

export function evaluateCopyReportPreflight(values) {
  if (!values?.id) {
    return { ready: false, reason: "missing_id" };
  }
  return { ready: true, reason: null };
}

function defaultExecutionSteps(t, withChecklist) {
  const steps = [
    {
      key: "preflight",
      label: t("operation_feedback.copy_report.step.preflight"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "copy",
      label: t("operation_feedback.copy_report.step.copy"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "checklist",
      label: t("operation_feedback.copy_report.step.checklist"),
      status: withChecklist ? STEP_STATUS.PENDING : STEP_STATUS.NOT_RELEVANT,
    },
  ];
  return steps;
}

/**
 * @param {object} args
 * @param {object} args.values - mapped copy payload
 * @param {object} [args.report]
 * @param {string|number} [args.companyId]
 * @param {Function} [args.afterCopy] - async (newReport) => void; throws on hard fail
 * @param {boolean} [args.hasChecklistStep]
 * @param {Function} [args.onCreated]
 * @param {Function} args.t
 * @param {Function} [args.copyFn]
 */
export async function runCopyReportOperation({
  values,
  report,
  companyId,
  afterCopy,
  hasChecklistStep = false,
  onCreated,
  t,
  copyFn = copyReport,
}) {
  const coordinator = getOperationCoordinator();
  const resourceId = values?.id || report?.id;
  const title = t("operation_feedback.copy_report.title", {
    number: report?.report_number || report?.displayTitle || resourceId || "",
  });

  const op = coordinator.start({
    operationKey: COPY_REPORT_OPERATION_KEY,
    title,
    presentationOwner: "dialog",
    resourceId,
    companyId,
    steps: defaultExecutionSteps(t, hasChecklistStep),
  });
  const id = op.clientOperationId;
  let rev = 1;

  const preflight = evaluateCopyReportPreflight(values);
  if (
    !canConfirmStepFromEvidence({
      fromReadiness: true,
      status: STEP_STATUS.COMPLETED,
    })
  ) {
    /* intentional */
  }

  if (!preflight.ready) {
    const detail = t("operation_feedback.copy_report.preflight_failed");
    coordinator.updateStep(id, "preflight", {
      status: STEP_STATUS.NEEDS_FIX,
      detail,
      revision: rev++,
    });
    coordinator.updateStep(id, "copy", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.updateStep(id, "checklist", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: detail,
      outcome: OUTCOME.NOT_APPLIED,
    });
    return { clientOperationId: id, ok: false, error: { kind: "preflight" } };
  }

  coordinator.updateStep(id, "preflight", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.copy_report.preflight_ok"),
    revision: rev++,
  });

  coordinator.updateStep(id, "copy", {
    status: STEP_STATUS.RUNNING,
    revision: rev++,
  });

  let response;
  try {
    response = await copyFn(values);
  } catch (err) {
    const normalized = normalizeError(err, {
      operationKey: COPY_REPORT_OPERATION_KEY,
    });
    coordinator.updateStep(id, "copy", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "checklist", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized };
  }

  const payload = response?.data ?? response;
  if (
    hasMeaningfulErrors(payload?.errors) ||
    payload?.error === true ||
    !payload?.id
  ) {
    const normalized = normalizeError(
      { response: { status: response?.status || 422, data: payload } },
      { operationKey: COPY_REPORT_OPERATION_KEY }
    );
    coordinator.updateStep(id, "copy", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "checklist", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      error: {
        safeMessage: normalized.safeMessage,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized, payload };
  }

  const links = [
    {
      href: `/dashboard/service/reports/${payload.id}`,
      label: t("operation_feedback.copy_report.open_report", {
        id: payload.id,
      }),
    },
  ];

  coordinator.updateStep(id, "copy", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.copy_report.copy_ok"),
    resultRef: { id: payload.id },
    revision: rev++,
  });

  let checklistFailed = false;
  if (hasChecklistStep && typeof afterCopy === "function") {
    coordinator.updateStep(id, "checklist", {
      status: STEP_STATUS.RUNNING,
      revision: rev++,
    });
    try {
      await afterCopy(payload);
      coordinator.updateStep(id, "checklist", {
        status: STEP_STATUS.COMPLETED,
        detail: t("operation_feedback.copy_report.checklist_ok"),
        revision: rev++,
      });
    } catch (checkErr) {
      checklistFailed = true;
      const normalized = normalizeError(checkErr, {
        operationKey: COPY_REPORT_OPERATION_KEY,
      });
      coordinator.updateStep(id, "checklist", {
        status: STEP_STATUS.FAILED,
        detail: normalized.safeMessage,
        revision: rev++,
      });
    }
  } else {
    coordinator.updateStep(id, "checklist", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
  }

  if (typeof onCreated === "function") {
    try {
      onCreated(payload);
    } catch (navErr) {
      console.error(navErr);
    }
  }

  coordinator.complete(id, {
    summary: checklistFailed
      ? t("operation_feedback.copy_report.partial_checklist")
      : t("operation_feedback.copy_report.success"),
    outcome: checklistFailed ? OUTCOME.PARTIAL : OUTCOME.APPLIED,
    links,
  });
  return {
    clientOperationId: id,
    ok: true,
    payload,
    checklistFailed,
  };
}
